import React, { useCallback, useRef, useState } from "react";
import { FiPaperclip, FiPlus, FiTrash2 } from "react-icons/fi";

import SlideOverPanel from "../../components/SlideOverPanel";
import { TIPOS_TERCEIRO } from "../../services/compras";

/**
 * Pergunta, no envio da lista ao estoque, se o ambiente tem itens de TERCEIROS
 * (serralheria, vidraçaria, tapeçaria...) e coleta o detalhamento de cada um.
 *
 * Por que aqui e não numa tela à parte: é neste momento que o projetista ainda
 * tem o ambiente na cabeça. Perguntar depois vira "eu depois eu cadastro" — e
 * o comprador descobre a serralheria faltando quando o móvel já está montado.
 *
 * Exposto como hook porque o botão que dispara o envio vive dentro de
 * `FolhasImpressao`, que tem 3 consumidores e não pode mudar de assinatura. O
 * `pedirTerceiros()` devolve uma Promise que só resolve quando o usuário
 * responde — o botão fica desabilitado sozinho enquanto isso, porque quem
 * chama já faz `await`.
 *
 *   const { pedirTerceiros, painelTerceiros } = usePainelTerceiros();
 *   const itens = await pedirTerceiros();   // null = desistiu
 */

const ITEM_VAZIO = { descricao: "", tipo_terceiro: "", observacao: "", arquivo: null };

export function usePainelTerceiros() {
  const [aberto, setAberto] = useState(false);
  const resolverRef = useRef(null);

  const pedirTerceiros = useCallback(() => {
    setAberto(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const responder = useCallback((valor) => {
    setAberto(false);
    const resolver = resolverRef.current;
    resolverRef.current = null;
    if (resolver) resolver(valor);
  }, []);

  const painelTerceiros = (
    <PainelTerceiros
      open={aberto}
      onResponder={responder}
    />
  );

  return { pedirTerceiros, painelTerceiros };
}

export default function PainelTerceiros({ open, onResponder }) {
  const [temTerceiros, setTemTerceiros] = useState(null); // null | true | false
  const [itens, setItens] = useState([{ ...ITEM_VAZIO }]);
  const [erros, setErros] = useState({});

  function reiniciar() {
    setTemTerceiros(null);
    setItens([{ ...ITEM_VAZIO }]);
    setErros({});
  }

  function alterar(idx, campo, valor) {
    setItens((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
    setErros((prev) => {
      const n = { ...prev };
      delete n[`i${idx}_${campo}`];
      return n;
    });
  }

  function validar() {
    const novos = {};
    itens.forEach((it, idx) => {
      // Descrição, tipo e anexo continuam obrigatórios — sem o detalhamento o
      // comprador não consegue nem pedir orçamento ao fornecedor.
      // OBSERVAÇÕES é OPCIONAL (pedido do dono do produto, 2026-08-06): nem
      // todo item de terceiro tem detalhe a acrescentar além do PDF.
      if (!it.descricao.trim()) novos[`i${idx}_descricao`] = true;
      if (!it.tipo_terceiro) novos[`i${idx}_tipo_terceiro`] = true;
      if (!it.arquivo) novos[`i${idx}_arquivo`] = true;
    });
    setErros(novos);
    return Object.keys(novos).length === 0;
  }

  function confirmarSim() {
    if (!validar()) return;
    const payload = itens.map((it) => ({
      descricao: it.descricao.trim(),
      tipo_terceiro: it.tipo_terceiro,
      observacao: it.observacao.trim(),
      arquivo: it.arquivo,
    }));
    reiniciar();
    onResponder(payload);
  }

  function confirmarNao() {
    reiniciar();
    onResponder([]);
  }

  function cancelar() {
    reiniciar();
    onResponder(null);
  }

  return (
    <SlideOverPanel
      open={open}
      onClose={cancelar}
      title="Itens de terceiros"
      subtitle="Antes de enviar ao estoque: este ambiente tem algo contratado fora?"
      width="min(95vw, 660px)"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>
          Serralheria, perfil de alumínio com pintura, portas e frentes de alumínio
          com vidro, vidraçaria, tapeçaria e ferragens especiais não são separados
          pelo estoque — vão direto para o setor de Compras.
        </p>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className={temTerceiros === true ? "btn btn--primary" : "btn btn--secondary"}
            onClick={() => setTemTerceiros(true)}
          >
            Sim, tem
          </button>
          <button
            type="button"
            className={temTerceiros === false ? "btn btn--primary" : "btn btn--secondary"}
            onClick={() => setTemTerceiros(false)}
          >
            Não tem
          </button>
        </div>

        {temTerceiros === true ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {itens.map((it, idx) => (
              <div
                key={idx}
                style={{
                  border: "1px solid var(--border)", borderRadius: 10,
                  padding: 12, display: "flex", flexDirection: "column", gap: 10,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <b style={{ fontSize: 13 }}>Item {idx + 1}</b>
                  {itens.length > 1 ? (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      aria-label={`Remover item ${idx + 1}`}
                      onClick={() => setItens((p) => p.filter((_, i) => i !== idx))}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  ) : null}
                </div>

                <Campo label="Descrição" erro={erros[`i${idx}_descricao`]}>
                  <input
                    className="input"
                    maxLength={300}
                    placeholder="Ex.: porta de alumínio com vidro 800x2100"
                    value={it.descricao}
                    onChange={(e) => alterar(idx, "descricao", e.target.value)}
                  />
                </Campo>

                <Campo label="Tipo de terceiro" erro={erros[`i${idx}_tipo_terceiro`]}>
                  <select
                    className="input"
                    value={it.tipo_terceiro}
                    onChange={(e) => alterar(idx, "tipo_terceiro", e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {TIPOS_TERCEIRO.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </Campo>

                <Campo
                  label="Detalhamento (arquivo)"
                  erro={erros[`i${idx}_arquivo`]}
                >
                  <label
                    className="btn btn--secondary"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                  >
                    <FiPaperclip size={13} />
                    {it.arquivo ? it.arquivo.name : "Escolher arquivo"}
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={(e) => alterar(idx, "arquivo", e.target.files?.[0] || null)}
                    />
                  </label>
                </Campo>

                <Campo label="Observações (opcional)">
                  <textarea
                    className="input"
                    rows={2}
                    maxLength={300}
                    placeholder="Ex.: vidro fumê, puxador embutido"
                    value={it.observacao}
                    onChange={(e) => alterar(idx, "observacao", e.target.value)}
                  />
                </Campo>
              </div>
            ))}

            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setItens((p) => [...p, { ...ITEM_VAZIO }])}
            >
              <FiPlus size={13} /> Adicionar item
            </button>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button type="button" className="btn btn--secondary" onClick={cancelar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={temTerceiros === null}
            onClick={temTerceiros ? confirmarSim : confirmarNao}
          >
            Enviar ao estoque
          </button>
        </div>
      </div>
    </SlideOverPanel>
  );
}

function Campo({ label, erro, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 }}>
      <span style={{ color: erro ? "var(--danger-text)" : "var(--ink-3)" }}>
        {label} {erro ? "— obrigatório" : null}
      </span>
      {children}
    </label>
  );
}
